import React from 'react';
import { Descriptions, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  DataQualityReport,
  EvaluationRun,
} from './sweetSpotEvaluationEngine';
import type { SweetSpotEvaluationResult, SweetSpotQualityInterval } from './types';
import { RUN_STATUS_LABELS } from './sweetSpotPresentation';
import { WEIGHT_SCHEME_LABELS } from './sweetSpotWeightSchemes';

type Props = {
  result: SweetSpotEvaluationResult | null;
  quality: DataQualityReport;
  run?: EvaluationRun;
};

const intervalColumns: ColumnsType<SweetSpotQualityInterval> = [
  { title: '顶深（m）', dataIndex: 'top', width: 110, render: (value: number) => value.toFixed(1) },
  { title: '底深（m）', dataIndex: 'bottom', width: 110, render: (value: number) => value.toFixed(1) },
  { title: '平均 SSI', dataIndex: 'avgScore', width: 110, render: (value: number) => value.toFixed(3) },
];

export default function SweetSpotResultInspector({ result, quality, run }: Props) {
  const highQuality = result?.highQualityIntervals ?? [];
  const poorQuality = result?.poorQualityIntervals ?? [];
  const boundaries = result?.recommendedStageBoundaries ?? [];
  const candidates = result?.recommendedClusterCandidates ?? [];

  return <section className="sweetspot-result-inspector sweetspot-result-detail-view">
      <header className="sweetspot-result-detail-header">
        <strong>工程分段方案</strong>
        <span className="sweetspot-collapse-meta">
          高品质井段 {highQuality.length} 段 · 低品质井段 {poorQuality.length} 段
          {' · '}推荐段界 {boundaries.length} 处 · 候选簇位置 {candidates.length} 个
        </span>
      </header>
      {result ? <div className="sweetspot-result-inspector-grid">
        <section>
          <h4>高品质井段</h4>
          <Table<SweetSpotQualityInterval>
            size="small" rowKey={(row) => String(row.top) + '-' + String(row.bottom)}
            columns={intervalColumns} dataSource={highQuality} pagination={false}
            locale={{ emptyText: '未识别到连续高品质井段' }}
          />
          <h4>低品质井段</h4>
          <Table<SweetSpotQualityInterval>
            size="small" rowKey={(row) => String(row.top) + '-' + String(row.bottom)}
            columns={intervalColumns} dataSource={poorQuality} pagination={false}
            locale={{ emptyText: '未识别到连续低品质井段' }}
          />
        </section>
        <section>
          <h4>推荐段界与候选簇位置</h4>
          <Table
            size="small"
            rowKey={(row) => String(row.top) + '-' + String(row.bottom)}
            dataSource={boundaries}
            pagination={false}
            columns={[
              { title: '推荐分段（m）', render: (_, row) => row.top.toFixed(1) + '–' + row.bottom.toFixed(1) },
              { title: '依据', dataIndex: 'reason' },
            ]}
            locale={{ emptyText: '无推荐分段结果' }}
          />
          <Table
            className="sweetspot-candidate-table"
            size="small"
            rowKey="depth"
            dataSource={candidates}
            pagination={false}
            columns={[
              { title: '候选簇位置（m）', dataIndex: 'depth', width: 140, render: (value: number) => value.toFixed(1) },
              { title: 'SSI', dataIndex: 'score', width: 90, render: (value: number) => value.toFixed(3) },
              { title: '依据', dataIndex: 'reason' },
            ]}
            locale={{ emptyText: '无候选簇位置' }}
          />
        </section>
        <section className="sweetspot-trace-section">
          <h4>数据质量与结果追溯</h4>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="数据覆盖率">{(quality.completeness * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label="有效指标">{quality.availableIndicatorIds.length} 项</Descriptions.Item>
            <Descriptions.Item label="评价方法">甜点指数 SSI</Descriptions.Item>
            <Descriptions.Item label="权重方案">{run ? WEIGHT_SCHEME_LABELS[run.config.weightStrategy] : WEIGHT_SCHEME_LABELS.fixed}</Descriptions.Item>
            <Descriptions.Item label="结果版本">
              <Tag>{run ? RUN_STATUS_LABELS[run.status] : '预览结果'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="生成时间">
              {run ? new Date(run.createdAt).toLocaleString() : '尚未执行评价'}
            </Descriptions.Item>
            <Descriptions.Item label="输入曲线" span={2}>
              {result.sourceCurveIds.join('、') || '未记录'}
            </Descriptions.Item>
          </Descriptions>
        </section>
      </div> : <div className="sweetspot-professional-empty">尚无可追溯的评价结果，请先执行评价。</div>}
  </section>;
}
